import { CartesianMappingProduct } from "../util/cartesianMapping";
import { NuxtPath } from "./nuxtPath";

export class NuxtPage {
  #def;
  #locale;
  #nuxtPath;

  /**
   *
   * @param {import("./pages.config").PageDescriptor} def
   * @param {import("../locale").Locale} locale
   */
  constructor(def, locale) {
    this.#def = def;
    this.#locale = locale;
    this.#nuxtPath = new NuxtPath(this.#def.localizedPages[this.#locale.code]);
  }

  /**
   * Resolves a single URL based on the provided SEO name.
   * @param {{[key: string]: { seoName: string }}} data - Object containing the SEO name to resolve.
   * @returns {NuxtPageInstance} - An instance of NuxtPageInstance with the resolved URL and variables.
   */
  resolveUrl(data = {}) {
    this.#validateResolveUrlData(data);

    const resolveData = this.#getResolveData(data);
    const resolvedUrl = this.#nuxtPath.resolve(resolveData);
    return new NuxtPageInstance(this, resolvedUrl, data);
  }

  /**
   *
   * @param {{ [key: string]: {seoName: string}[] }} data
   */
  resolveUrls(data) {
    this.#validateResolveUrlsData(data);
    const resolvingData = CartesianMappingProduct.getProduct(data);
    const instances = resolvingData.map((dataInstance) => {
      const resolveData = this.#getResolveData(dataInstance);
      const resolvedUrl = this.#nuxtPath.resolve(resolveData);
      return new NuxtPageInstance(this, resolvedUrl, dataInstance);
    });
    return instances;
  }

  #validateResolveUrlsData(data) {
    if (typeof data !== "object" || data === null) {
      throw new TypeError("data must be an object");
    }
    const requiredVariables = this.#nuxtPath.pathVariables;
    requiredVariables.forEach((variable) => {
      if (!Object.hasOwn(data, variable)) {
        throw new Error(
          `Data object is missing required key: '${variable}' for path: ${
            this.#nuxtPath.path
          }`
        );
      }
      if (!Array.isArray(data[variable])) {
        throw new TypeError(
          `Data for variable '${variable}' must be an array, but got: ${typeof data[
            variable
          ]}`
        );
      }
    });
  }

  #validateResolveUrlData(data) {
    if (typeof data !== "object" || data === null) {
      throw new TypeError("data must be an object");
    }
    const requiredVariables = this.#nuxtPath.pathVariables;
    requiredVariables.forEach((variable) => {
      if (!Object.hasOwn(data, variable)) {
        throw new Error(
          `Data object is missing required key: '${variable}' for path: ${
            this.#nuxtPath.path
          }`
        );
      }
    });
  }

  #getResolveData(data) {
    const resolveData = {};
    this.#nuxtPath.pathVariables.forEach((variable) => {
      resolveData[variable] = data[variable].seoName;
    });
    return resolveData;
  }

  get pathVariables() {
    return this.#nuxtPath.pathVariables;
  }

  /**
   * By default, a page is indexed.
   * @returns {boolean} - true if the page should be no-indexed, false otherwise.
   */
  get isNoIndex() {
    if (!Object.hasOwn(this.#def, "sitemapConfig")) {
      return false; // default value
    }
    const sitemapConfig = this.#def.sitemapConfig;
    if (!Object.hasOwn(sitemapConfig, "noIndex")) {
      return false;
    }
    if (typeof sitemapConfig.noIndex === "boolean") {
      return sitemapConfig.noIndex;
    }
    if (Object.hasOwn(sitemapConfig.noIndex, this.#locale.code)) {
      return sitemapConfig.noIndex[this.#locale.code] === true;
    } else {
      // if the locale is not defined, we assume it's indexed
      return false;
    }
  }

  get hasNoIndexDecorator() {
    return (
      Object.hasOwn(this.#def, "sitemapConfig") &&
      Object.hasOwn(this.#def.sitemapConfig, "noIndexDecorator")
    );
  }

  get noIndexDecorator() {
    if (this.hasNoIndexDecorator) {
      return this.#def.sitemapConfig.noIndexDecorator;
    }
    return null;
  }

  get changefreq() {
    if (
      Object.hasOwn(this.#def, "sitemapConfig") &&
      Object.hasOwn(this.#def.sitemapConfig, "changefreq")
    ) {
      return this.#def.sitemapConfig.changefreq;
    }
    return "weekly"; // default value
  }

  get priority() {
    if (
      Object.hasOwn(this.#def, "sitemapConfig") &&
      Object.hasOwn(this.#def.sitemapConfig, "priority")
    ) {
      return this.#def.sitemapConfig.priority;
    }
    return 0.9; // default value
  }
}

// class with resolved URL and concrete variables that were used to resolve it
export class NuxtPageInstance {
  #nuxtPage;
  #resolvedUrl;
  #variables;

  /**
   *
   * @param {NuxtPage} nuxtPage
   * @param {NuxtPath} resolvedUrl
   * @param {{ [key: string]: Object }} variables
   */
  constructor(nuxtPage, resolvedUrl, variables) {
    this.#nuxtPage = nuxtPage;
    this.#resolvedUrl = resolvedUrl;
    this.#variables = variables;
  }

  get resolvedUrl() {
    return this.#resolvedUrl;
  }

  get variables() {
    return this.#variables;
  }

  /**
   * @return {boolean} - true if the page should be excluded from indexing, false otherwise
   */
  get isNoIndex() {
    return (
      this.#nuxtPage.isNoIndex ||
      (this.#nuxtPage.hasNoIndexDecorator &&
        this.#nuxtPage.noIndexDecorator(this.#variables))
    );
  }

  getOverrideableNoIndex(overrideFuntion) {
    const isNoIndex = this.#nuxtPage.isNoIndex;
    if (!isNoIndex && typeof overrideFuntion === "function") {
      return overrideFuntion(this.#variables);
    }
    return isNoIndex;
  }
}
